import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { NavLink, Link } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import './style.css';
import './photos-map.css';
import { loadMapPhotos } from './mapPhotos';

const SECTIONS = [
  { key: 'writing', label: 'Writing', href: '/' },
  { key: 'photos', label: 'Map', href: '/photos' },
  { key: 'lunch', label: 'Lunch', href: '/lunch' },
  { key: 'resume', label: 'Résumé', href: '/resume' },
];

function SiteNav() {
  return (
    <nav className="site-nav" aria-label="Sections">
      {SECTIONS.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 ? <span className="site-nav-sep" aria-hidden="true">·</span> : null}
          <NavLink
            to={s.href}
            end={s.href === '/'}
            className={({ isActive }) => 'site-nav-link' + (isActive ? ' active' : '')}
          >
            {s.label}
          </NavLink>
        </React.Fragment>
      ))}
    </nav>
  );
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
// Real API photos carry public_id; the offline mock fallback carries a
// numeric id instead — accept either as the lightbox lookup key.
function photoKey(p) {
  return p.public_id != null ? p.public_id : p.id;
}

const TILE_ATTR = '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · © <a href="https://carto.com/attributions">CARTO</a>';
// CARTO now requires a basemap key on the raster tiles or they serve a
// watermarked version. Key is domain-restricted and ships in the client
// bundle by design (same model as Mapbox/Google Maps keys).
const CARTO_KEY = process.env.REACT_APP_CARTO_KEY;
function tileUrl(theme) {
  const style = theme === 'dark' ? 'dark_all' : 'light_all';
  const q = CARTO_KEY ? `?key=${CARTO_KEY}` : '';
  return `https://{s}.basemaps.cartocdn.com/${style}/{z}/{x}/{y}{r}.png${q}`;
}

// Groups the pinned photos into two flat lists for the search typeahead —
// cities (name + country, centroid = average lat/lng of that city's photos)
// and countries (name + city count, centroid = average of all its photos).
// Rebuilt whenever the photo list changes, so the index always reflects
// what's actually on the map.
function buildSearchIndex(list) {
  const cityMap = new Map();
  const countryMap = new Map();
  list.forEach((p) => {
    const ck = p.city + '|' + p.country;
    if (!cityMap.has(ck)) cityMap.set(ck, { city: p.city, country: p.country, lats: [], lngs: [], count: 0 });
    const c = cityMap.get(ck);
    c.lats.push(p.lat); c.lngs.push(p.lng); c.count++;

    if (!countryMap.has(p.country)) countryMap.set(p.country, { lats: [], lngs: [], count: 0, cities: new Set() });
    const co = countryMap.get(p.country);
    co.lats.push(p.lat); co.lngs.push(p.lng); co.count++; co.cities.add(p.city);
  });

  const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
  const places = [];
  cityMap.forEach((c) => {
    places.push({ type: 'city', name: c.city, sub: c.country, count: c.count, latlng: [avg(c.lats), avg(c.lngs)], zoom: 10 });
  });
  countryMap.forEach((co, country) => {
    places.push({
      type: 'country',
      name: country,
      sub: co.cities.size + (co.cities.size === 1 ? ' city' : ' cities'),
      count: co.count,
      latlng: [avg(co.lats), avg(co.lngs)],
      zoom: co.cities.size > 1 ? 5 : 9,
    });
  });
  places.sort((a, b) => b.count - a.count);
  return places;
}

function Highlight({ text, query }) {
  if (!query) return text;
  const i = text.toLowerCase().indexOf(query.toLowerCase());
  if (i < 0) return text;
  return <>{text.slice(0, i)}<b>{text.slice(i, i + query.length)}</b>{text.slice(i + query.length)}</>;
}

// Rough continent anchors so the loading skeleton hints at the real spread.
const SKELETON_ANCHORS = [
  [50, 10],    // Europe
  [30, 78],    // South Asia
  [35, 138],   // East Asia
  [1, 103],    // SE Asia
  [40, -95],   // North America
  [19, -99],   // Central America
  [-30, 22],   // Southern Africa
  [31, -8],    // North Africa
];

export default function PhotosPage({ theme, onToggleTheme }) {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Map · Sandeep';
    return () => { document.title = prev; };
  }, []);

  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const tilesRef = useRef(null);
  const markersBuiltRef = useRef(false);
  const clusterRef = useRef(null);
  const skeletonDoneRef = useRef(false);
  const skipNextThemeSwapRef = useRef(true);
  const photoIndexByIdRef = useRef(new Map());

  const [loaded, setLoaded] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [zoomed, setZoomed] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null); // index into sortedPhotos | null
  const [statusText, setStatusText] = useState('Locating photos…');
  const [skeletonPoints, setSkeletonPoints] = useState([]);
  const [showLoader, setShowLoader] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Reuses the shared cached fetch — if a homepage visit already kicked
    // this off via prefetchMapPhotos(), this resolves immediately.
    loadMapPhotos().then((mapped) => {
      if (cancelled) return;
      setPhotos(mapped);
      if (mapped.length === 0) {
        setLoaded(true);
      } else {
        setStatusText('Placing pins…');
      }
    });
    return () => { cancelled = true; };
  }, []);

  // init the map once on mount — map-first, so the skeleton loader has
  // real tiles + projected pixel coords to sit on while photos are fetched
  useEffect(() => {
    if (!mapElRef.current) return;

    const map = L.map(mapElRef.current, {
      zoomControl: false,
      fadeAnimation: false,
      worldCopyJump: true,
      minZoom: 2,
      maxZoom: 18,
      zoomSnap: 0.5,
      wheelPxPerZoomLevel: 120,
    });
    mapRef.current = map;
    L.control.zoom({ position: 'topright' }).addTo(map);

    const tiles = L.tileLayer(tileUrl(theme), { subdomains: 'abcd', maxZoom: 19, attribution: TILE_ATTR }).addTo(map);
    tilesRef.current = tiles;

    map.setView([28, 12], 2);

    const updateSkeleton = () => {
      if (skeletonDoneRef.current) return;
      setSkeletonPoints(SKELETON_ANCHORS.map(([lat, lng]) => {
        const pt = map.latLngToContainerPoint(L.latLng(lat, lng));
        return { x: pt.x, y: pt.y };
      }));
    };
    updateSkeleton();
    map.on('zoomend moveend', updateSkeleton);

    map.on('zoomend moveend', () => setZoomed(map.getZoom() > 4.5));

    map.on('popupopen', (e) => {
      const img = e.popup.getElement().querySelector('.pop-photo');
      if (img) {
        img.addEventListener('click', () => {
          const idx = photoIndexByIdRef.current.get(img.dataset.id);
          if (idx != null) setLightboxIndex(idx);
        });
      }
    });

    return () => {
      map.remove();
      mapRef.current = null;
      tilesRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // place pins + clusters once photos arrive, then reveal (dissolve skeleton)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || photos.length === 0 || markersBuiltRef.current) return;
    markersBuiltRef.current = true;

    function pinHtml(p) {
      return `<div class="photo-pin"><img src="${p.thumb}" alt="" loading="lazy"/></div>`;
    }
    const cluster = L.markerClusterGroup({
      maxClusterRadius: 54,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      spiderLegPolylineOptions: { weight: 1, color: '#8a857a', opacity: 0.6 },
      iconCreateFunction: (cl) => {
        const rep = cl.getAllChildMarkers()[0].options.photo;
        const count = cl.getChildCount();
        const big = count >= 8 ? ' big' : '';
        const html =
          '<div class="photo-cluster' + big + '">' +
            '<div class="pc-stack"></div>' +
            '<div class="pc-photo"><img src="' + rep.thumb + '" alt="" loading="lazy"/></div>' +
            '<div class="pc-count">' + count + '</div>' +
          '</div>';
        const s = count >= 8 ? 60 : 52;
        return L.divIcon({ html, className: 'photo-cluster-icon', iconSize: [s, s], iconAnchor: [s / 2, s / 2] });
      },
    });

    const bounds = [];
    photos.forEach((p) => {
      const icon = L.divIcon({ html: pinHtml(p), className: 'photo-pin-icon', iconSize: [44, 44], iconAnchor: [22, 22], popupAnchor: [0, -20] });
      const m = L.marker([p.lat, p.lng], { icon, photo: p });
      const popHtml =
        '<div class="pop-card">' +
          '<img class="pop-photo" src="' + p.thumb.replace(/w_\d+,h_\d+/, 'w_500,h_375') + '" data-id="' + photoKey(p) + '" alt=""/>' +
          '<div class="pop-meta">' +
            '<div class="pop-loc">' + p.city + '</div>' +
            '<div class="pop-sub"><span class="pop-country">' + p.country + '</span><span class="pop-date">' + fmtDate(p.date) + '</span></div>' +
          '</div>' +
        '</div>';
      m.bindPopup(popHtml, { closeButton: false, offset: [0, -6], minWidth: 216, maxWidth: 216, autoPanPadding: [40, 40] });
      cluster.addLayer(m);
      bounds.push([p.lat, p.lng]);
    });
    map.addLayer(cluster);
    map.fitBounds(bounds, { padding: [70, 70] });
    clusterRef.current = cluster;

    skeletonDoneRef.current = true;
    setLoaded(true);
  }, [photos]);

  // once revealed, dissolve then unmount the skeleton loader
  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(() => setShowLoader(false), 560);
    return () => clearTimeout(t);
  }, [loaded]);

  // swap tile layer when the site theme changes (skip the initial mount —
  // the map-init effect above already applied the starting theme)
  useEffect(() => {
    if (skipNextThemeSwapRef.current) {
      skipNextThemeSwapRef.current = false;
      return;
    }
    const map = mapRef.current;
    if (!map) return;
    const prevTiles = tilesRef.current;
    const next = L.tileLayer(tileUrl(theme), { subdomains: 'abcd', maxZoom: 19, attribution: TILE_ATTR }).addTo(map);
    const swap = () => {
      if (prevTiles && prevTiles !== next) map.removeLayer(prevTiles);
      tilesRef.current = next;
    };
    next.once('load', swap);
    const t = setTimeout(swap, 400);
    return () => clearTimeout(t);
  }, [theme]);

  const resetView = useCallback(() => {
    const map = mapRef.current;
    if (!map || photos.length === 0) return;
    map.fitBounds(photos.map(p => [p.lat, p.lng]), { padding: [70, 70] });
  }, [photos]);

  const hudCities = new Set(photos.map(p => p.city)).size;
  const hudCountries = new Set(photos.map(p => p.country)).size;

  // chronological order for lightbox prev/next, keyed by public_id so a
  // popup click can find its place regardless of fetch/render order
  const sortedPhotos = useMemo(
    () => [...photos].sort((a, b) => (a.date || '').localeCompare(b.date || '')),
    [photos]
  );
  const photoIndexById = useMemo(() => {
    const m = new Map();
    sortedPhotos.forEach((p, i) => m.set(String(photoKey(p)), i));
    return m;
  }, [sortedPhotos]);
  useEffect(() => { photoIndexByIdRef.current = photoIndexById; }, [photoIndexById]);

  const lightboxPhoto = lightboxIndex != null ? sortedPhotos[lightboxIndex] : null;

  const gotoLightbox = useCallback((delta) => {
    setLightboxIndex((i) => {
      if (i == null) return i;
      const next = i + delta;
      return next >= 0 && next < sortedPhotos.length ? next : i;
    });
  }, [sortedPhotos.length]);

  useEffect(() => {
    if (lightboxIndex == null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      else if (e.key === 'ArrowRight') gotoLightbox(1);
      else if (e.key === 'ArrowLeft') gotoLightbox(-1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightboxIndex, gotoLightbox]);

  // preload neighbouring full-size images so prev/next feels instant
  useEffect(() => {
    if (lightboxIndex == null) return;
    [lightboxIndex - 1, lightboxIndex + 1].forEach((i) => {
      const p = sortedPhotos[i];
      if (p) { const img = new Image(); img.src = p.full; }
    });
  }, [lightboxIndex, sortedPhotos]);

  // ---- search: jump to a city or country instead of panning/zooming ----
  const searchWrapRef = useRef(null);
  const searchInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResultsVisible, setSearchResultsVisible] = useState(false);

  const searchPlaces = useMemo(() => buildSearchIndex(photos), [photos]);
  const trimmedQuery = searchQuery.trim();
  const searchMatches = trimmedQuery
    ? searchPlaces
        .filter(p => p.name.toLowerCase().includes(trimmedQuery.toLowerCase()) || p.sub.toLowerCase().includes(trimmedQuery.toLowerCase()))
        .slice(0, 8)
    : [];

  const selectPlace = useCallback((p) => {
    const map = mapRef.current;
    if (!map) return;
    map.flyTo(p.latlng, p.zoom, { duration: 0.9 });
    setSearchQuery(p.name);
    setSearchResultsVisible(false);
    if (searchInputRef.current) searchInputRef.current.blur();
    if (p.type === 'city') {
      map.once('moveend', () => {
        const cluster = clusterRef.current;
        if (!cluster) return;
        let nearest = null, bestDist = Infinity;
        cluster.eachLayer((m) => {
          const d = map.distance(m.getLatLng(), L.latLng(p.latlng));
          if (d < bestDist) { bestDist = d; nearest = m; }
        });
        if (nearest) {
          if (cluster.getVisibleParent && cluster.getVisibleParent(nearest) !== nearest) {
            cluster.zoomToShowLayer(nearest, () => nearest.openPopup());
          } else {
            nearest.openPopup();
          }
        }
      });
    }
  }, []);

  // clicking outside the search box collapses the results but keeps the
  // typed query, so glancing at the map doesn't lose your last search
  useEffect(() => {
    function onDocClick(e) {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setSearchResultsVisible(false);
      }
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  return (
    <div className="map-page">
      <header className="site-header">
        <Link className="wordmark" to="/" aria-label="Home">
          sandeep<span className="wordmark-dot">.</span>
        </Link>
        <div className="site-nav-wrap">
          <SiteNav />
        </div>
        <button
          className="theme-toggle"
          onClick={onToggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
        >
          {theme === 'dark' ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round">
              <circle cx="8" cy="8" r="3" />
              <path d="M8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.3 3.3l1 1M11.7 11.7l1 1M3.3 12.7l1-1M11.7 4.3l1-1" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13.5 9.5A6 6 0 1 1 6.5 2.5a5 5 0 0 0 7 7z" />
            </svg>
          )}
        </button>
      </header>

      <div className="map-shell">
        <div id="map" ref={mapElRef} />

        {(!loaded || photos.length > 0) && (
          <div className={'map-hud' + (zoomed ? ' zoomed' : '') + (loaded ? ' loaded' : '')}>
            <h1>Passport Stamps</h1>
            <div className="hud-stats">
              <span className="hud-stat accent"><b>{loaded ? photos.length : '—'}</b> photos</span>
              <span className="hud-sep">·</span>
              <span className="hud-stat"><b>{loaded ? hudCities : '—'}</b> cities</span>
              <span className="hud-sep">·</span>
              <span className="hud-stat"><b>{loaded ? hudCountries : '—'}</b> countries</span>
            </div>
            <div className="hud-hint">Each pin is a place I've shot. Zoom into a country to split its cities; click a pin to see the photo.</div>
            {!loaded && (
              <div className="hud-status"><span className="hs-dot" />{statusText}</div>
            )}
            {loaded && zoomed && (
              <button className="hud-reset" onClick={resetView}>↺ Back to the world</button>
            )}
          </div>
        )}

        {showLoader && (
          <div className={'map-loader' + (loaded ? ' done' : '')} aria-hidden="true">
            {skeletonPoints.map((p, i) => (
              <div
                key={i}
                className="skeleton-dot"
                style={{ left: p.x, top: p.y, animationDelay: `${i * 0.12}s` }}
              />
            ))}
          </div>
        )}

        {loaded && photos.length === 0 && (
          <div className="map-empty">
            <p>No geotagged photos yet. Once new shots with GPS data are uploaded, they'll show up here.</p>
          </div>
        )}

        {loaded && photos.length > 0 && (
          <div className={'map-search' + (searchResultsVisible ? ' has-query' : '')} ref={searchWrapRef}>
            <div className="map-search-box">
              <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
                <circle cx="9" cy="9" r="6.5" />
                <path d="M14 14l4 4" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                placeholder="Search a city or country…"
                autoComplete="off"
                aria-label="Search cities or countries"
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSearchResultsVisible(e.target.value.trim().length > 0);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchMatches.length) selectPlace(searchMatches[0]);
                  else if (e.key === 'Escape') {
                    setSearchQuery('');
                    setSearchResultsVisible(false);
                    e.target.blur();
                  }
                }}
              />
              <button
                className="map-search-clear"
                onClick={() => {
                  setSearchQuery('');
                  setSearchResultsVisible(false);
                  if (searchInputRef.current) searchInputRef.current.focus();
                }}
                aria-label="Clear search"
              >
                <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                  <path d="M5 5l10 10M15 5L5 15" />
                </svg>
              </button>
            </div>
            <div className="map-search-results">
              {searchResultsVisible && (
                searchMatches.length ? (
                  searchMatches.map((p, i) => (
                    <button
                      key={p.type + '|' + p.name}
                      className={'msr-item' + (i === 0 ? ' active' : '')}
                      onClick={() => selectPlace(p)}
                    >
                      <span className="msr-name">
                        <Highlight text={p.name} query={trimmedQuery} />
                        {p.type === 'city' ? ', ' + p.sub : ''}
                      </span>
                      <span className="msr-meta">{p.count}{p.count === 1 ? ' photo' : ' photos'}</span>
                    </button>
                  ))
                ) : (
                  <div className="msr-empty">No places match "{trimmedQuery}"</div>
                )
              )}
            </div>
          </div>
        )}
      </div>

      {lightboxPhoto && (
        <div className="map-lightbox open" aria-hidden="false">
          <div className="mlb-backdrop" onClick={() => setLightboxIndex(null)} />
          <button className="mlb-close" onClick={() => setLightboxIndex(null)} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
          </button>
          <button
            className="mlb-nav mlb-prev"
            onClick={() => gotoLightbox(-1)}
            disabled={lightboxIndex <= 0}
            aria-label="Previous photo"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 6l-6 6 6 6" />
            </svg>
          </button>
          <button
            className="mlb-nav mlb-next"
            onClick={() => gotoLightbox(1)}
            disabled={lightboxIndex >= sortedPhotos.length - 1}
            aria-label="Next photo"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
          <figure>
            <img key={lightboxPhoto.full} src={lightboxPhoto.full} alt="" />
            <figcaption>
              {lightboxPhoto.city}, {lightboxPhoto.country}
              <span className="mlb-date">{fmtDate(lightboxPhoto.date)}</span>
              <span className="mlb-counter">{lightboxIndex + 1} / {sortedPhotos.length}</span>
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}
